/**
 * Block Explorer Utilities
 * Maps blockchain networks to their block explorer URLs
 */

export interface BlockExplorerConfig {
  name: string;
  txUrl: (hash: string) => string;
  addressUrl: (address: string) => string;
}

// Mapping of chain/exchange names to block explorer configurations
export const BLOCK_EXPLORERS: Record<string, BlockExplorerConfig> = {
  // Ethereum chains
  ethereum: {
    name: 'Etherscan',
    txUrl: (hash) => `https://etherscan.io/tx/${hash}`,
    addressUrl: (address) => `https://etherscan.io/address/${address}`,
  },
  eth: {
    name: 'Etherscan',
    txUrl: (hash) => `https://etherscan.io/tx/${hash}`,
    addressUrl: (address) => `https://etherscan.io/address/${address}`,
  },
  metamask: {
    name: 'Etherscan',
    txUrl: (hash) => `https://etherscan.io/tx/${hash}`,
    addressUrl: (address) => `https://etherscan.io/address/${address}`,
  },

  // Polygon
  polygon: {
    name: 'PolygonScan',
    txUrl: (hash) => `https://polygonscan.com/tx/${hash}`,
    addressUrl: (address) => `https://polygonscan.com/address/${address}`,
  },
  matic: {
    name: 'PolygonScan',
    txUrl: (hash) => `https://polygonscan.com/tx/${hash}`,
    addressUrl: (address) => `https://polygonscan.com/address/${address}`,
  },

  // Arbitrum
  arbitrum: {
    name: 'Arbiscan',
    txUrl: (hash) => `https://arbiscan.io/tx/${hash}`,
    addressUrl: (address) => `https://arbiscan.io/address/${address}`,
  },

  // Optimism
  optimism: {
    name: 'Optimistic Etherscan',
    txUrl: (hash) => `https://optimistic.etherscan.io/tx/${hash}`,
    addressUrl: (address) => `https://optimistic.etherscan.io/address/${address}`,
  },

  // Base
  base: {
    name: 'BaseScan',
    txUrl: (hash) => `https://basescan.org/tx/${hash}`,
    addressUrl: (address) => `https://basescan.org/address/${address}`,
  },

  // BSC/Binance Smart Chain
  bsc: {
    name: 'BscScan',
    txUrl: (hash) => `https://bscscan.com/tx/${hash}`,
    addressUrl: (address) => `https://bscscan.com/address/${address}`,
  },
  binance: {
    name: 'BscScan',
    txUrl: (hash) => `https://bscscan.com/tx/${hash}`,
    addressUrl: (address) => `https://bscscan.com/address/${address}`,
  },

  // Avalanche
  avalanche: {
    name: 'SnowTrace',
    txUrl: (hash) => `https://snowtrace.io/tx/${hash}`,
    addressUrl: (address) => `https://snowtrace.io/address/${address}`,
  },
  avax: {
    name: 'SnowTrace',
    txUrl: (hash) => `https://snowtrace.io/tx/${hash}`,
    addressUrl: (address) => `https://snowtrace.io/address/${address}`,
  },

  // Fantom
  fantom: {
    name: 'FTMScan',
    txUrl: (hash) => `https://ftmscan.com/tx/${hash}`,
    addressUrl: (address) => `https://ftmscan.com/address/${address}`,
  },
  ftm: {
    name: 'FTMScan',
    txUrl: (hash) => `https://ftmscan.com/tx/${hash}`,
    addressUrl: (address) => `https://ftmscan.com/address/${address}`,
  },

  // Solana
  solana: {
    name: 'Solscan',
    txUrl: (hash) => `https://solscan.io/tx/${hash}`,
    addressUrl: (address) => `https://solscan.io/address/${address}`,
  },
  sol: {
    name: 'Solscan',
    txUrl: (hash) => `https://solscan.io/tx/${hash}`,
    addressUrl: (address) => `https://solscan.io/address/${address}`,
  },
  phantom: {
    name: 'Solscan',
    txUrl: (hash) => `https://solscan.io/tx/${hash}`,
    addressUrl: (address) => `https://solscan.io/address/${address}`,
  },

  // Blast
  blast: {
    name: 'Blastscan',
    txUrl: (hash) => `https://blastscan.io/tx/${hash}`,
    addressUrl: (address) => `https://blastscan.io/address/${address}`,
  },

  // Mantle
  mantle: {
    name: 'Mantlescan',
    txUrl: (hash) => `https://mantlescan.xyz/tx/${hash}`,
    addressUrl: (address) => `https://mantlescan.xyz/address/${address}`,
  },

  // Starknet
  starknet: {
    name: 'Starkscan',
    txUrl: (hash) => `https://starkscan.co/tx/${hash}`,
    addressUrl: (address) => `https://starkscan.co/contract/${address}`,
  },

  // Metis
  metis: {
    name: 'Andromeda Explorer',
    txUrl: (hash) => `https://andromeda-explorer.metis.io/tx/${hash}`,
    addressUrl: (address) => `https://andromeda-explorer.metis.io/address/${address}`,
  },

  // opBNB
  opbnb: {
    name: 'opBNBScan',
    txUrl: (hash) => `https://opbnbscan.com/tx/${hash}`,
    addressUrl: (address) => `https://opbnbscan.com/address/${address}`,
  },

  // Avalanche Subnets
  'avalanche-subnets': {
    name: 'Subnet Explorer',
    txUrl: (hash) => `https://subnets.avax.network/tx/${hash}`,
    addressUrl: (address) => `https://subnets.avax.network/address/${address}`,
  },

  // Harmony
  harmony: {
    name: 'Harmony Explorer',
    txUrl: (hash) => `https://explorer.harmony.one/tx/${hash}`,
    addressUrl: (address) => `https://explorer.harmony.one/address/${address}`,
  },
  one: {
    name: 'Harmony Explorer',
    txUrl: (hash) => `https://explorer.harmony.one/tx/${hash}`,
    addressUrl: (address) => `https://explorer.harmony.one/address/${address}`,
  },

  // Celo
  celo: {
    name: 'Celoscan',
    txUrl: (hash) => `https://celoscan.io/tx/${hash}`,
    addressUrl: (address) => `https://celoscan.io/address/${address}`,
  },

  // Bitcoin (using Blockstream as primary)
  bitcoin: {
    name: 'Blockstream',
    txUrl: (hash) => `https://blockstream.info/tx/${hash}`,
    addressUrl: (address) => `https://blockstream.info/address/${address}`,
  },
  btc: {
    name: 'Blockstream',
    txUrl: (hash) => `https://blockstream.info/tx/${hash}`,
    addressUrl: (address) => `https://blockstream.info/address/${address}`,
  },

  // Bitcoin Cash
  bitcoincash: {
    name: 'Blockchair',
    txUrl: (hash) => `https://blockchair.com/bitcoin-cash/transaction/${hash}`,
    addressUrl: (address) => `https://blockchair.com/bitcoin-cash/address/${address}`,
  },
  bch: {
    name: 'Blockchair',
    txUrl: (hash) => `https://blockchair.com/bitcoin-cash/transaction/${hash}`,
    addressUrl: (address) => `https://blockchair.com/bitcoin-cash/address/${address}`,
  },

  // Litecoin
  litecoin: {
    name: 'Blockchair',
    txUrl: (hash) => `https://blockchair.com/litecoin/transaction/${hash}`,
    addressUrl: (address) => `https://blockchair.com/litecoin/address/${address}`,
  },
  ltc: {
    name: 'Blockchair',
    txUrl: (hash) => `https://blockchair.com/litecoin/transaction/${hash}`,
    addressUrl: (address) => `https://blockchair.com/litecoin/address/${address}`,
  },

  // Cosmos Hub
  cosmos: {
    name: 'Mintscan',
    txUrl: (hash) => `https://www.mintscan.io/cosmos/txs/${hash}`,
    addressUrl: (address) => `https://www.mintscan.io/cosmos/account/${address}`,
  },
  atom: {
    name: 'Mintscan',
    txUrl: (hash) => `https://www.mintscan.io/cosmos/txs/${hash}`,
    addressUrl: (address) => `https://www.mintscan.io/cosmos/account/${address}`,
  },

  // Osmosis
  osmosis: {
    name: 'Mintscan',
    txUrl: (hash) => `https://www.mintscan.io/osmosis/txs/${hash}`,
    addressUrl: (address) => `https://www.mintscan.io/osmosis/account/${address}`,
  },
  osmo: {
    name: 'Mintscan',
    txUrl: (hash) => `https://www.mintscan.io/osmosis/txs/${hash}`,
    addressUrl: (address) => `https://www.mintscan.io/osmosis/account/${address}`,
  },

  // Injective
  injective: {
    name: 'Mintscan',
    txUrl: (hash) => `https://www.mintscan.io/injective/txs/${hash}`,
    addressUrl: (address) => `https://www.mintscan.io/injective/account/${address}`,
  },
  inj: {
    name: 'Mintscan',
    txUrl: (hash) => `https://www.mintscan.io/injective/txs/${hash}`,
    addressUrl: (address) => `https://www.mintscan.io/injective/account/${address}`,
  },

  // Secret Network
  secret: {
    name: 'Secret Explorer',
    txUrl: (hash) => `https://secretnodes.com/secret/transaction/${hash}`,
    addressUrl: (address) => `https://secretnodes.com/secret/account/${address}`,
  },
  scrt: {
    name: 'Secret Explorer',
    txUrl: (hash) => `https://secretnodes.com/secret/transaction/${hash}`,
    addressUrl: (address) => `https://secretnodes.com/secret/account/${address}`,
  },

  // Near Protocol
  near: {
    name: 'Near Explorer',
    txUrl: (hash) => `https://explorer.near.org/transactions/${hash}`,
    addressUrl: (address) => `https://explorer.near.org/accounts/${address}`,
  },

  // Aptos
  aptos: {
    name: 'Aptos Explorer',
    txUrl: (hash) => `https://explorer.aptoslabs.com/txn/${hash}`,
    addressUrl: (address) => `https://explorer.aptoslabs.com/account/${address}`,
  },
  apt: {
    name: 'Aptos Explorer',
    txUrl: (hash) => `https://explorer.aptoslabs.com/txn/${hash}`,
    addressUrl: (address) => `https://explorer.aptoslabs.com/account/${address}`,
  },

  // Sui
  sui: {
    name: 'Suiscan',
    txUrl: (hash) => `https://suiscan.xyz/transaction/${hash}`,
    addressUrl: (address) => `https://suiscan.xyz/account/${address}`,
  },

  // Ton (The Open Network)
  ton: {
    name: 'Tonviewer',
    txUrl: (hash) => `https://tonviewer.com/transaction/${hash}`,
    addressUrl: (address) => `https://tonviewer.com/address/${address}`,
  },

  // Algorand
  algorand: {
    name: 'AlgoExplorer',
    txUrl: (hash) => `https://algoexplorer.io/tx/${hash}`,
    addressUrl: (address) => `https://algoexplorer.io/address/${address}`,
  },
  algo: {
    name: 'AlgoExplorer',
    txUrl: (hash) => `https://algoexplorer.io/tx/${hash}`,
    addressUrl: (address) => `https://algoexplorer.io/address/${address}`,
  },

  // Cardano
  cardano: {
    name: 'CardanoScan',
    txUrl: (hash) => `https://cardanoscan.io/transaction/${hash}`,
    addressUrl: (address) => `https://cardanoscan.io/address/${address}`,
  },
  ada: {
    name: 'CardanoScan',
    txUrl: (hash) => `https://cardanoscan.io/transaction/${hash}`,
    addressUrl: (address) => `https://cardanoscan.io/address/${address}`,
  },

  // Cronos
  cronos: {
    name: 'CronoScan',
    txUrl: (hash) => `https://cronoscan.com/tx/${hash}`,
    addressUrl: (address) => `https://cronoscan.com/address/${address}`,
  },
  cro: {
    name: 'CronoScan',
    txUrl: (hash) => `https://cronoscan.com/tx/${hash}`,
    addressUrl: (address) => `https://cronoscan.com/address/${address}`,
  },

  // Gnosis
  gnosis: {
    name: 'Gnosisscan',
    txUrl: (hash) => `https://gnosisscan.io/tx/${hash}`,
    addressUrl: (address) => `https://gnosisscan.io/address/${address}`,
  },
  xdai: {
    name: 'Gnosisscan',
    txUrl: (hash) => `https://gnosisscan.io/tx/${hash}`,
    addressUrl: (address) => `https://gnosisscan.io/address/${address}`,
  },

  // zkSync Era
  zksync: {
    name: 'zkSync Explorer',
    txUrl: (hash) => `https://explorer.zksync.io/tx/${hash}`,
    addressUrl: (address) => `https://explorer.zksync.io/address/${address}`,
  },
  'zksync-era': {
    name: 'zkSync Explorer',
    txUrl: (hash) => `https://explorer.zksync.io/tx/${hash}`,
    addressUrl: (address) => `https://explorer.zksync.io/address/${address}`,
  },

  // Linea
  linea: {
    name: 'LineaScan',
    txUrl: (hash) => `https://lineascan.build/tx/${hash}`,
    addressUrl: (address) => `https://lineascan.build/address/${address}`,
  },

  // Scroll
  scroll: {
    name: 'Scrollscan',
    txUrl: (hash) => `https://scrollscan.com/tx/${hash}`,
    addressUrl: (address) => `https://scrollscan.com/address/${address}`,
  },
};

/**
 * Get the block explorer URL for a transaction
 * @param chain - The blockchain/exchange name (e.g., 'ethereum', 'polygon', 'solana')
 * @param txHash - The transaction hash
 * @returns The full URL to view the transaction on the block explorer, or null if not supported
 */
export const getBlockExplorerTxUrl = (
  chain: string,
  txHash: string
): string | null => {
  if (!chain || !txHash) return null;

  const chainKey = chain.toLowerCase().trim();
  const explorer = BLOCK_EXPLORERS[chainKey];

  if (!explorer) return null;

  return explorer.txUrl(txHash);
};

/**
 * Get the block explorer URL for an address
 * @param chain - The blockchain/exchange name
 * @param address - The wallet/contract address
 * @returns The full URL to view the address on the block explorer, or null if not supported
 */
export const getBlockExplorerAddressUrl = (
  chain: string,
  address: string
): string | null => {
  if (!chain || !address) return null;

  const chainKey = chain.toLowerCase().trim();
  const explorer = BLOCK_EXPLORERS[chainKey];

  if (!explorer) return null;

  return explorer.addressUrl(address);
};

/**
 * Get the name of the block explorer for a given chain
 * @param chain - The blockchain/exchange name
 * @returns The name of the block explorer (e.g., 'Etherscan', 'PolygonScan')
 */
export const getBlockExplorerName = (chain: string): string | null => {
  if (!chain) return null;

  const chainKey = chain.toLowerCase().trim();
  const explorer = BLOCK_EXPLORERS[chainKey];

  return explorer?.name || null;
};

/**
 * Check if a chain has block explorer support
 * @param chain - The blockchain/exchange name
 * @returns True if block explorer is available for this chain
 */
export const hasBlockExplorer = (chain: string): boolean => {
  if (!chain) return false;
  return !!BLOCK_EXPLORERS[chain.toLowerCase().trim()];
};

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise that resolves when text is copied
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-HTTPS
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      textArea.remove();
      return success;
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};
